import React, { useState } from 'react';
import { aiAssistantApi, AiAssistantActionProposal } from '../../services/aiAssistantApi';

export const confirmationPoll = { attempts: 20, waitMs: 1000 };

type Phase = 'waiting' | 'pending' | 'unresolved' | 'executed' | 'not_executed';

function errorText(e: unknown, fallback: string) {
  const err = e as { response?: { data?: { message?: string; error?: string } }; message?: string };
  return err.response?.data?.error || err.response?.data?.message || (e instanceof Error ? e.message : fallback);
}

export function proposalPhase(proposal: AiAssistantActionProposal, local?: Phase): Phase {
  if (local) return local;
  if (proposal.receipt && (proposal.status === 'executed' || proposal.status === 'partial' || proposal.executionState === 'executed'))
    return 'executed';
  if (proposal.status === 'confirming' || proposal.executionState === 'pending') return 'pending';
  if (proposal.status === 'recovery_required' || proposal.executionState === 'unresolved') return 'unresolved';
  if (proposal.executionState === 'not_executed') return 'not_executed';
  return 'waiting';
}

export function ProposalCards({
  proposals,
  chatId,
  onSettled,
  onUpdated,
}: {
  proposals: AiAssistantActionProposal[];
  chatId: number | null;
  onSettled: (id: number, receipt: string) => void;
  onUpdated?: (proposal: AiAssistantActionProposal) => void;
}) {
  const [reason, setReason] = useState<Record<number, string>>({});
  const [approver, setApprover] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [localPhase, setLocalPhase] = useState<Record<number, Phase>>({});
  const visible = proposals.filter((row) => proposalPhase(row, localPhase[row.id]) !== 'executed' || row.receipt);
  if (visible.length === 0) return null;

  const applyPolled = (proposal: AiAssistantActionProposal, polled: AiAssistantActionProposal) => {
    const phase = proposalPhase(polled);
    setLocalPhase((current) => ({ ...current, [proposal.id]: phase }));
    onUpdated?.(polled);
    if (phase === 'executed' && polled.receipt) {
      onSettled(proposal.id, polled.receipt);
      return;
    }
    if (phase === 'not_executed' || (polled.status === 'proposed' && !polled.receipt)) {
      setLocalPhase((current) => ({ ...current, [proposal.id]: 'not_executed' }));
      setError('');
      return;
    }
    if (phase === 'unresolved') {
      setError('');
      return;
    }
    setError('');
  };

  return (
    <div className="mt-3 space-y-2 border-t border-neutral-200 pt-2">
      {visible.map((proposal) => {
        const phase = proposalPhase(proposal, localPhase[proposal.id]);
        const locked = phase === 'pending' || phase === 'unresolved';
        return (
          <div key={proposal.id} className="space-y-1.5 rounded-md bg-white px-2 py-2">
            <p className="text-xs font-medium text-neutral-800">{proposal.title}</p>
            <p className="text-xs text-neutral-600">{proposal.receipt || proposal.summary}</p>
            {phase === 'pending' && <p className="text-[11px] text-amber-800">Your action is still processing.</p>}
            {phase === 'unresolved' && <p className="text-[11px] text-amber-800">This action is being verified. Do not confirm again.</p>}
            {phase === 'not_executed' && <p className="text-[11px] text-neutral-600">The change was not saved. You can confirm again.</p>}
            {proposal.warnings?.length > 0 && !proposal.receipt && phase === 'waiting' && (
              <p className="text-[11px] text-amber-800">{proposal.warnings.join(' ')}</p>
            )}
            {phase !== 'executed' && (
              <div className="flex flex-col gap-1.5">
                {proposal.requiresReason && phase !== 'pending' && phase !== 'unresolved' && (
                  <input
                    value={reason[proposal.id] ?? ''}
                    onChange={(event) => setReason((current) => ({ ...current, [proposal.id]: event.target.value }))}
                    placeholder="Reason"
                    className="w-full rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs text-neutral-800"
                  />
                )}
                {proposal.secondApprover && phase !== 'pending' && phase !== 'unresolved' && (
                  <input
                    value={approver[proposal.id] ?? ''}
                    onChange={(event) => setApprover((current) => ({ ...current, [proposal.id]: event.target.value }))}
                    placeholder="Second approver employee id"
                    className="w-full rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs text-neutral-800"
                  />
                )}
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    disabled={locked || busy != null
                      || (proposal.requiresReason && !(reason[proposal.id] ?? '').trim())
                      || (proposal.secondApprover && !(Number(approver[proposal.id]) > 0))}
                    onClick={() => {
                      setBusy(proposal.id);
                      setError('');
                      setLocalPhase((current) => ({ ...current, [proposal.id]: 'pending' }));
                      const second = Number(approver[proposal.id]);
                      void aiAssistantApi.confirmAction(proposal.id, {
                        reason: (reason[proposal.id] ?? '').trim() || undefined,
                        secondApproverId: Number.isFinite(second) && second > 0 ? second : undefined,
                        idempotencyKey: `proposal-${proposal.id}`,
                        chatId,
                      }).then((saved) => {
                        if (!saved.receipt) {
                          const message = saved.error || 'The change was not saved.';
                          const lower = message.toLowerCase();
                          const unresolved = lower.includes('do not confirm') || lower.includes('being verified');
                          const pending = lower.includes('in progress') || lower.includes('still open') || lower.includes('still processing');
                          setLocalPhase((current) => ({
                            ...current,
                            [proposal.id]: unresolved ? 'unresolved' : pending ? 'pending' : 'not_executed',
                          }));
                          setError(message);
                          return;
                        }
                        setLocalPhase((current) => ({ ...current, [proposal.id]: 'executed' }));
                        onSettled(proposal.id, saved.receipt);
                      }).catch(async (e) => {
                        const timedOut = (e as { code?: string })?.code === 'ECONNABORTED'
                          || (e instanceof Error && e.message.toLowerCase().includes('timeout'));
                        if (!timedOut) {
                          setLocalPhase((current) => ({ ...current, [proposal.id]: 'waiting' }));
                          setError(errorText(e, 'The change was not saved.'));
                          return;
                        }
                        setLocalPhase((current) => ({ ...current, [proposal.id]: 'pending' }));
                        for (let attempt = 0; attempt < confirmationPoll.attempts; attempt++) {
                          if (confirmationPoll.waitMs > 0)
                            await new Promise((resolve) => setTimeout(resolve, confirmationPoll.waitMs));
                          try {
                            const polled = await aiAssistantApi.getProposal(proposal.id);
                            const phaseNow = proposalPhase(polled);
                            if (phaseNow === 'executed' && polled.receipt) {
                              applyPolled(proposal, polled);
                              return;
                            }
                            if (phaseNow === 'unresolved' || polled.status === 'proposed') {
                              applyPolled(proposal, polled);
                              return;
                            }
                          } catch {
                            /* keep polling until the window ends */
                          }
                        }
                        setLocalPhase((current) => ({ ...current, [proposal.id]: 'pending' }));
                        setError('Your action is still processing.');
                      }).finally(() => setBusy(null));
                    }}
                    className="rounded-full bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {busy === proposal.id || phase === 'pending' ? 'Saving…' : 'Confirm'}
                  </button>
                  <button
                    type="button"
                    disabled={locked || busy != null}
                    onClick={() => {
                      if (locked) return;
                      setBusy(proposal.id);
                      setError('');
                      void aiAssistantApi.rejectAction(proposal.id).then(() => {
                        onSettled(proposal.id, 'Cancelled. Nothing was saved.');
                      }).catch((e) => {
                        setError(errorText(e, 'The proposal could not be cancelled.'));
                      }).finally(() => setBusy(null));
                    }}
                    className="rounded-full border border-neutral-300 bg-white px-2.5 py-1 text-xs font-medium text-neutral-800 hover:bg-neutral-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  {locked && (
                    <button
                      type="button"
                      disabled={busy != null}
                      onClick={() => {
                        setBusy(proposal.id);
                        void aiAssistantApi.getProposal(proposal.id)
                          .then((polled) => applyPolled(proposal, polled))
                          .catch((e) => setError(errorText(e, 'The status could not be checked.')))
                          .finally(() => setBusy(null));
                      }}
                      className="rounded-full border border-neutral-300 bg-white px-2.5 py-1 text-xs font-medium text-neutral-800 hover:bg-neutral-50 disabled:opacity-50"
                    >
                      Check status
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
      {error && <p className="text-[11px] text-red-700">{error}</p>}
    </div>
  );
}
